/**
 * Combatant lifecycle — spawn, remove, and the lossless order-preserving restore.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok } from "@lancer-vtt/shared";
import type { Combatant, EncounterState } from "../types";
import { changed, clamp, requireCombatant, type EncounterHandler } from "./context";

export const applyAddCombatant: EncounterHandler<"add_combatant"> = (state, cmd) => {
  if (state.combatants[cmd.combatant.id]) {
    return err(new CommandError(`add_combatant: combatant ${cmd.combatant.id} already exists`));
  }
  const c: Combatant = cmd.combatant;
  const next: EncounterState = {
    ...state,
    combatants: { ...state.combatants, [c.id]: c },
    order: [...state.order, c.id],
  };
  return ok({
    state: next,
    inverse: {
      type: "remove_combatant",
      actorId: cmd.actorId,
      combatantId: c.id,
    },
    events: changed(c.id),
  });
};

export const applyRemoveCombatant: EncounterHandler<"remove_combatant"> = (state, cmd) => {
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const orderIndex = state.order.indexOf(cmd.combatantId);
  const { [cmd.combatantId]: _removed, ...rest } = state.combatants;
  const nextOrder = state.order.filter((id) => id !== cmd.combatantId);
  // No turn-pointer bookkeeping — there is no turn pointer: `order` is stable display
  // order, and the activation machine derives "who's
  // next" from sides + the acted set, never an index. A removed unit naturally drops
  // out of the derived pools; its (now-dangling) id in `activated` is harmless. (A
  // unit removed while it is `activating` is a GM edge case recovered via the
  // `set_activation` override — the reducer doesn't auto-clear it.)
  const next: EncounterState = { ...state, combatants: rest, order: nextOrder };
  // Lossless inverse: splice the captured combatant back at its order index
  // (re-adding via add_combatant would append to the end, losing position).
  return ok({
    state: next,
    inverse: {
      type: "restore_combatant",
      actorId: cmd.actorId,
      combatant: c,
      orderIndex: orderIndex === -1 ? state.order.length : orderIndex,
    },
    events: changed(cmd.combatantId),
  });
};

export const applyRestoreCombatant: EncounterHandler<"restore_combatant"> = (state, cmd) => {
  // Internal inverse of remove_combatant: splice the captured combatant back
  // in at its prior order index. Its own inverse is remove_combatant, so
  // undo/redo of a removal round-trips exactly.
  const c = cmd.combatant;
  if (state.combatants[c.id]) {
    return err(new CommandError(`restore_combatant: combatant ${c.id} already exists`));
  }
  const idx = clamp(cmd.orderIndex, 0, state.order.length);
  const nextOrder = [...state.order.slice(0, idx), c.id, ...state.order.slice(idx)];
  const next: EncounterState = {
    ...state,
    combatants: { ...state.combatants, [c.id]: c },
    order: nextOrder,
  };
  return ok({
    state: next,
    inverse: {
      type: "remove_combatant",
      actorId: cmd.actorId,
      combatantId: c.id,
    },
    events: changed(c.id),
  });
};
